import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FlaskConical, Plus, Search, ChevronRight, Trash2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import SprayNav from '../../components/spray/SprayNav';
import Button from '../../components/ui/Button';
import sprayService from '../../services/spray';
import type { Chemical } from '../../types/spray';
import CreateChemicalFromLookupModal from '../../components/spray/CreateChemicalFromLookupModal';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { toNumOr } from '@/utils/numeric';

const SIGNAL_WORD_COLORS: Record<string, string> = {
  DANGER:  'bg-red-100 text-red-700',
  WARNING: 'bg-orange-100 text-orange-700',
  CAUTION: 'bg-yellow-100 text-yellow-700',
};

function ChemicalRegistry() {
  const navigate = useNavigate();
  const isAdmin = useModuleLicense('spray', 'admin').hasAccess;
  const canCreate = useModuleLicense('spray', ['creator', 'admin']).hasAccess;

  const [chemicals, setChemicals] = useState<Chemical[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [showLookupCreate, setShowLookupCreate] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const LIMIT = 20;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery]);

  const loadChemicals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await sprayService.searchChemicals(debouncedQuery, page, LIMIT);
      if (res.success) {
        setChemicals(res.data || []);
        setTotal(res.total || 0);
      }
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery, page]);

  useEffect(() => {
    loadChemicals();
  }, [loadChemicals]);

  const handleDelete = async (chem: Chemical) => {
    const ok = window.confirm(
      `Remove "${chem.product_name}" from your chemicals?\n\n` +
      'If the chemical is referenced by sprays, inventory or registrations it ' +
      'will be hidden (deactivated) so the history stays intact; otherwise it ' +
      'is removed permanently.',
    );
    if (!ok) return;
    setDeleteError(null);
    setDeletingId(chem.id);
    const res = await sprayService.deleteChemical(chem.id);
    setDeletingId(null);
    if (!res.success) {
      setDeleteError(res.error?.message || `Could not remove ${chem.product_name}`);
      return;
    }
    loadChemicals();
  };

  return (
    <>
      <PageHeader
        title="Chemical Registry"
        subtitle="Your organisation's own products, added from the APVMA register"
        icon={<FlaskConical className="h-6 w-6 text-white" />}
        actionButtons={
          canCreate ? (
            <div className="flex gap-2">
              <Button variant="primary" size="sm" onClick={() => setShowLookupCreate(true)}>
                <Plus className="h-4 w-4" />
                Create chemical
              </Button>
            </div>
          ) : undefined
        }
      />
      <SprayNav />
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by product name, trade name, or active ingredient..."
            className="w-full pl-10 pr-4 py-2.5 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {deleteError && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-800 rounded-lg px-3 py-2 text-sm">
            {deleteError}
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-20 bg-neutral-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : chemicals.length === 0 ? (
          <div className="text-center py-16">
            <FlaskConical className="h-12 w-12 mx-auto mb-3 text-neutral-300" />
            <p className="font-medium text-neutral-600">No chemicals found</p>
            <p className="text-sm text-neutral-400 mt-1">
              {debouncedQuery ? 'Try a different search term.' : 'Create the first chemical from the APVMA register.'}
            </p>
            {!debouncedQuery && canCreate && (
              <Button variant="primary" className="mt-4" onClick={() => setShowLookupCreate(true)}>
                <Plus className="h-4 w-4" /> Create chemical
              </Button>
            )}
          </div>
        ) : (
          <>
            <p className="text-sm text-neutral-500 mb-3">
              {total} product{total !== 1 ? 's' : ''} found
            </p>
            <div className="space-y-2">
              {chemicals.map(chem => (
                <div
                  key={chem.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/spray/chemicals/${chem.id}`)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/spray/chemicals/${chem.id}`); } }}
                  className="w-full text-left bg-white border border-neutral-200 rounded-lg px-4 py-3 hover:border-primary-300 hover:bg-primary-50 transition-colors flex items-center justify-between gap-3 cursor-pointer"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-neutral-800">{chem.product_name}</span>
                      {chem.trade_name && (
                        <span className="text-sm text-neutral-500">({chem.trade_name})</span>
                      )}
                      {chem.signal_word && (
                        <span className={`text-xs font-bold px-1.5 py-0.5 rounded uppercase ${
                        SIGNAL_WORD_COLORS[chem.signal_word] || 'bg-neutral-100 text-neutral-600'
                      }`}>
                          {chem.signal_word}
                        </span>
                      )}
                      {!chem.apvma_approval_number && (
                        <span className="text-[10px] uppercase tracking-wide bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded">
                          Legacy
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-neutral-400 flex-wrap">
                      {chem.manufacturer && <span>{chem.manufacturer}</span>}
                      {chem.formulation_type && <span>· {chem.formulation_type}</span>}
                      {chem.active_ingredients.length > 0 && (
                        <span>· {chem.active_ingredients.map(ai => ai.name).join(', ')}</span>
                      )}
                      {toNumOr(chem.registration_count) > 0 && (
                        <span className="text-primary-600">
                          · {toNumOr(chem.registration_count)} crop registration{toNumOr(chem.registration_count) !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {isAdmin && (
                      <button
                        type="button"
                        title="Remove chemical (admin)"
                        disabled={deletingId === chem.id}
                        onClick={(e) => { e.stopPropagation(); handleDelete(chem); }}
                        className="p-1.5 rounded text-neutral-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                    <ChevronRight className="h-4 w-4 text-neutral-300" />
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {total > LIMIT && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                  Previous
                </Button>
                <span className="text-sm text-neutral-500">
                  Page {page} of {Math.ceil(total / LIMIT)}
                </span>
                <Button variant="outline" size="sm" disabled={page * LIMIT >= total} onClick={() => setPage(p => p + 1)}>
                  Next
                </Button>
              </div>
            )}
          </>
        )}

        {showLookupCreate && (
          <CreateChemicalFromLookupModal
            onClose={() => setShowLookupCreate(false)}
            onCreated={(id) => {
              setShowLookupCreate(false);
              navigate(`/spray/chemicals/${id}`);
            }}
          />
        )}
      </div>
    </>
  );
}

export default ChemicalRegistry;
